<script setup>
import { computed, nextTick, onMounted, reactive, ref, toRef } from 'vue'
import * as libPhoneNumber from 'libphonenumber-js'
import { vMaska } from 'maska'
import { useField, useSubmitCount } from 'vee-validate'
import { formatPhoneInput } from '@/utils/phoneInput.js'

const EMOJI_ERROR_MESSAGE = 'Использование эмодзи недопустимо'
const EMOJI_TEST_REGEX = /\p{Extended_Pictographic}/u
const EMOJI_STRIP_REGEX = /\p{Extended_Pictographic}/gu
const SCROLL_SYNC_EVENTS = [
  'input',
  'keydown',
  'keyup',
  'focus',
  'blur',
  'click',
  'change',
  'paste',
  'cut',
  'scroll',
  'wheel',
  'dragover',
]

const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  value: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: ''
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  inputmode: {
    type: String,
    default: undefined,
  },
  textInfo: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: Boolean,
    default: false,
  },
  // mask
  mask: {
    type: String,
    default: '',
  },
  pattern: {
    type: String,
    default: '',
  },
  showMask: {
    type: String,
    default: '',
  },
  paste: {
    type: Function,
  },
  dataMaskaTokens: {
    type: String,
  },
  dataMaskaEager: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false
  },
  disallowEmoji: {
    type: Boolean,
    default: false,
  },
})

// use `toRef` to create reactive references to `name` prop which is passed to `useField`
// this is important because vee-validte needs to know if the field name changes
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
const name = toRef(props, 'name')

const inputRef = ref(null)
const maskRef = ref(null)
const emojiRejected = ref(false)
const submitCount = useSubmitCount()

const state = reactive({
  focused: false,
})

// we don't provide any rules here because we are using form-level validation
// https://vee-validate.logaretm.com/v4/guide/validation#form-level-validation
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  meta,
} = useField(name, undefined, {
  initialValue: props.value,
})

const displayErrorMessage = computed(() => {
  if (props.disallowEmoji && emojiRejected.value) {
    return EMOJI_ERROR_MESSAGE
  }

  if (!submitCount.value) {
    return undefined
  }

  return errorMessage.value
})

const isAsYouTypePhone = computed(() => props.type === 'phone' && !props.mask)

const sharedInputBindings = computed(() => ({
  id: props.name,
  name: props.name,
  type: props.type,
  value: inputValue.value,
  placeholder: props.placeholder,
  inputmode: props.inputmode,
  pattern: props.pattern,
  disabled: props.disabled,
  autocomplete: props.autocomplete ? 'on' : 'off',
}))

function containsEmoji(value) {
  return EMOJI_TEST_REGEX.test(value)
}

function stripEmoji(value) {
  return value.replace(EMOJI_STRIP_REGEX, '')
}

function sanitizeEmoji(value) {
  if (!props.disallowEmoji) {
    emojiRejected.value = false
    return value
  }

  if (containsEmoji(value)) {
    emojiRejected.value = true
    return stripEmoji(value)
  }

  emojiRejected.value = false
  return value
}

function rejectEmojiAttempt(value) {
  emojiRejected.value = true
  commitValue(value)
}

function clearEmojiRejectedOnUserEdit(event) {
  if (event.data && !containsEmoji(event.data)) {
    emojiRejected.value = false
    return
  }

  if (
    event.inputType === 'deleteContentBackward'
    || event.inputType === 'deleteContentForward'
    || event.inputType === 'deleteByCut'
  ) {
    emojiRejected.value = false
  }
}

async function commitValue(value) {
  handleChange(value)
  await nextTick()
  if (inputRef.value && inputRef.value.value !== value) {
    inputRef.value.value = value
  }
}

function onBlur(event) {
  state.focused = false
  handleBlur(event)
  if (props.showMask) {
    if (inputValue.value === props.showMask) {
      handleChange('')
    }
  }
}

function onFocus() {
  state.focused = true
  if (props.showMask) {
    if (!inputValue.value) {
      handleChange(props.showMask)
    }
  }
}

function onPaste(e) {
  if (props.paste) {
    props.paste(e)
    nextTick(() => {
      const value = String(inputValue.value ?? '')
      commitValue(isAsYouTypePhone.value ? formatPhoneInput(value) : value)
    })
    return
  }

  const paste = (e.clipboardData || window.clipboardData).getData('text')
  const input = inputRef.value
  const current = String(inputValue.value ?? '')
  const start = input?.selectionStart ?? current.length
  const end = input?.selectionEnd ?? current.length
  const merged = `${current.slice(0, start)}${paste}${current.slice(end)}`

  if (props.disallowEmoji && containsEmoji(merged)) {
    rejectEmojiAttempt(sanitizeEmoji(merged))
    return
  }

  emojiRejected.value = false
  commitValue(isAsYouTypePhone.value ? formatPhoneInput(merged) : merged)
}

function onChange(event) {
  if (props.showMask) {
    event.preventDefault()
    event.stopPropagation()

    if (event.target.value.length < props.showMask.length && state.focused) {
      commitValue(props.showMask)
      return
    }
  }

  let rawValue = event.target.value
  const previousValue = String(inputValue.value ?? '')

  if (props.disallowEmoji) {
    if (containsEmoji(rawValue)) {
      rejectEmojiAttempt(stripEmoji(rawValue))
      return
    }

    if (event.data && containsEmoji(event.data)) {
      rejectEmojiAttempt(previousValue)
      return
    }

    clearEmojiRejectedOnUserEdit(event)
  }

  if (isAsYouTypePhone.value) {
    rawValue = formatPhoneInput(rawValue)
  }

  commitValue(rawValue)
}

function handleAutoFill(event) {
  try {
    if (props.type !== 'phone') {
      return;
    }
    const inputValue = event.target.value;
    const parsedPhone = libPhoneNumber.parsePhoneNumber(inputValue);
    if (parsedPhone.isValid()) {
      return;
    }

    const phoneWithPrefix = libPhoneNumber.parsePhoneNumber(`+7${parsedPhone.number.slice(1)}`);
    if (!inputValue.startsWith('+7') && phoneWithPrefix.isValid()) {
      handleChange(phoneWithPrefix.number);
      return;
    }
  } catch (e) {
    console.error('Error in handleAutoFill:', e);
  }
}

function onKeydown(event) {
  if (props.showMask && event.target.selectionStart < props.showMask.length) {
    event.target.focus()
    event.target.setSelectionRange(props.showMask.length, Math.max(props.showMask.length, event.target.selectionEnd))
    return
  }
  if (props.showMask) {
    if (event.target.value.length - 1 < props.showMask.length && (event.key === 'Backspace' || event.key === 'Delete')) {
      event.preventDefault()
      event.stopPropagation()
      commitValue(props.showMask)
      return
    }
  }

  if (!props.disallowEmoji) {
    handleChange(event)
  }
}

onMounted(() => {
  if (!props.showMask || !inputRef.value) {
    return
  }

  SCROLL_SYNC_EVENTS.forEach(eventName => {
    inputRef.value.addEventListener(eventName, (event) => {
      if (maskRef.value) {
        maskRef.value.scrollLeft = event.target.scrollLeft
      }
    })
  })
})

</script>

<template>
  <div
    class="textInput"
    :class="{ 'has-error': !!displayErrorMessage, success: meta.valid, fullWidth: fullWidth }"
  >
    <label
      v-if="label"
      :for="name"
      class="label"
    >
      {{ label }}
      <span
        v-if="required"
        class="star"
      >*</span>
    </label>
    <div
      class="inputWrapper"
    >
      <div
        v-if="showMask"
        ref="maskRef"
        class="mask"
      >
        <span
          class="left"
        >
          {{ showMask }}
        </span>
        <span
          class="right"
        >
          {{ inputValue?.replace(showMask, '') }}
        </span>
      </div>
      <input
        v-if="mask"
        ref="inputRef"
        v-maska
        v-bind="sharedInputBindings"
        :data-maska="mask"
        :data-maska-tokens="dataMaskaTokens"
        :data-maska-eager="dataMaskaEager"
        @input="onChange"
        @keydown="onKeydown"
        @blur="onBlur"
        @focus="onFocus"
        @paste.prevent="onPaste"
        @change="handleAutoFill"
      />
      <input
        v-else
        ref="inputRef"
        v-bind="sharedInputBindings"
        @input="onChange"
        @keydown="onKeydown"
        @blur="onBlur"
        @focus="onFocus"
        @paste.prevent="onPaste"
        @change="handleAutoFill"
      />
    </div>
    <div
      v-if="textInfo && !displayErrorMessage"
      class="textInfo"
    >
      {{ textInfo }}
    </div>
    <div
      class="errorMessage"
    >
      {{ displayErrorMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/variables";

.textInput {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.fullWidth {
    width: 100%;
  }

  label {
    color: var(--form-label-color);
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }

  .inputWrapper {
    width: 100%;
    position: relative;

    .mask {
      .left {
        color: #838181;
      }
    }

    .mask,
    input {
      box-sizing: border-box;
      width: 100%;
      border-radius: 12px;
      color: var(--text-secondary-color);
      border: 1px solid var(--form-border-base-color);
      background: var(--form-background-color, transparent);
      font-size: 16px;
      font-weight: 400;
      height: 48px;
      padding: 14px 12px;
      caret-color: var(--text-caret-color);

      &::placeholder {
        color: #838181;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0;
        text-align: left;
      }

      &:disabled {
        color: #838181;
        border: 1px solid #303030;
      }

      &:focus {
        border: 1px solid var(--form-border-active-color);
      }

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: var(--text-secondary-color, #fff);
        caret-color: var(--text-caret-color);
        border: 1px solid var(--form-border-base-color);
        // Браузер иначе красит фон в белый/голубой при autofill / вставке из подсказок
        transition: background-color 99999s ease-out;
        box-shadow: 0 0 0 1000px var(--form-background-color, #1F1F1F) inset !important;
      }
    }

    .mask + input {
      color: transparent;
      caret-color: var(--text-caret-color);
    }

    .mask {
      border: 1px solid transparent !important;
      position: absolute;
      pointer-events: none;
      white-space: nowrap;
      overflow: hidden;
      .left {
        color: #838181;
      }
    }
  }

  .inputError {
    border: 1px solid var(--form-border-active-color) !important;
  }

  .errorMessage {
    color: var(--text-error-color);
    font-size: 12px;
    font-weight: 500;
    min-height: 14px;
    line-height: 12px;
    margin-bottom: 4px;
  }

  .textInfo {
    color: #838181;
    font-size: 12px;
    font-weight: 500;
    min-height: 14px;
    line-height: 12px;
    margin-bottom: 4px;
  }

  .error_icon {
    position: absolute;
    top: calc(50%);
    right: 12px;
    transform: translateY(-50%);
    z-index: 100000;
  }
}

.TextInput input {
  color: #fff;
  border: 1px solid var(--form-border-base-color);

  &:focus {
    border: 1px solid var(--form-border-active-color);
  }
}

.label {
  display: flex;
  align-items: center;
  gap: 0px;
}

.star {
  color: var(--text-error-color);
  font-size: 20px;
  font-weight: bold;
  height: 10px;
}
</style>
